import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { authFetch } from "../services/authService";
import { 
  LayoutDashboard, 
  Calendar, 
  ClipboardCheck, 
  FileText, 
  BookOpen,
  Receipt, 
  Package, 
  Archive, 
  Newspaper, 
  User,
  LogOut,
  Settings,
  Download,
  Users,
  Truck,
  FileSpreadsheet,
  Car,
  Send,
  Shield,
  FileX,
  Bell,
  Building2,
  TrendingUp,
  Inbox,
  Wrench,
  Sun,
  Moon,
  Warehouse,
  BarChart3,
  CircleDot,
  Calculator,
  ClipboardList,
  Ticket,
  Award,
  HelpCircle,
  Recycle,
  MonitorPlay,
  Trash2,
  X
} from "lucide-react";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Button } from "../components/ui/button";
import SupportRequestDialog from "../components/SupportRequestDialog";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Menu items for OFFICINA users with badge keys
const officinaNavItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", badgeKey: null },
  { path: "/appointments", icon: Calendar, label: "Appuntamenti", badgeKey: "appointments" },
  { path: "/acceptances", icon: ClipboardCheck, label: "Accettazione", badgeKey: "acceptances" },
  { path: "/quotes", icon: FileText, label: "Preventivi", badgeKey: "quotes" },
  { path: "/work-schedule", icon: Calendar, label: "Agenda Lavori", badgeKey: null },
  { path: "/parts-requests", icon: Send, label: "Richieste Ricambi", badgeKey: "partsRequests" },
  { path: "/supplier-orders", icon: Package, label: "Ordini", badgeKey: "orders" },
  { path: "/work-orders", icon: Wrench, label: "Ordini di Lavoro", badgeKey: "workOrders" },
  { path: "/ddt", icon: ClipboardList, label: "DDT", badgeKey: null },
  { path: "/invoices", icon: Receipt, label: "Fatture", badgeKey: "invoices" },
  { path: "/credit-notes", icon: FileX, label: "Note di Credito", badgeKey: null },
  { path: "/fiscale", icon: Calculator, label: "Fiscale", badgeKey: null },
  { path: "/fiscale/dashboard", icon: BarChart3, label: "Dashboard Fiscale", badgeKey: null },
  { path: "/rentri", icon: Recycle, label: "RENTRI Rifiuti", badgeKey: null },
  { path: "/video-riparazioni", icon: MonitorPlay, label: "Video Riparazioni", badgeKey: null },
  { path: "/magazzino", icon: Warehouse, label: "Magazzino", badgeKey: "lowStock" },
  { path: "/gommista", icon: CircleDot, label: "Gommista", badgeKey: null },
  { path: "/suppliers", icon: Truck, label: "Fornitori", badgeKey: null },
  { path: "/customers", icon: Users, label: "Clienti", badgeKey: null },
  { path: "/vehicles", icon: Car, label: "Veicoli", badgeKey: null },
  { path: "/points", icon: Award, label: "Punti", badgeKey: null },
  { path: "/vouchers", icon: Ticket, label: "Voucher", badgeKey: null },
  { path: "/statistiche", icon: BarChart3, label: "Statistiche", badgeKey: null },
  { path: "/margini", icon: TrendingUp, label: "Margini Profitto", badgeKey: null },
  { path: "/archive", icon: Archive, label: "Archivio", badgeKey: null },
  { path: "/settings", icon: Wrench, label: "Configurazioni", badgeKey: null },
  { path: "/export", icon: FileSpreadsheet, label: "Esporta", badgeKey: null },
];

// Menu items for RICAMBISTA users
const ricambistaNavItems = [
  { path: "/ricambista", icon: TrendingUp, label: "Dashboard", badgeKey: null },
  { path: "/ricambista/richieste", icon: Inbox, label: "Richieste Preventivo", badgeKey: "richieste" },
  { path: "/ricambista/ordini-gestione", icon: Package, label: "Ordini da Gestire", badgeKey: "ordini" },
  { path: "/ricambista/officine", icon: Building2, label: "Archivio Officine", badgeKey: null },
  { path: "/ricambista/operatori", icon: Users, label: "Archivio Operatori", badgeKey: null },
];

export const DashboardLayout = ({ children, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [menuBadges, setMenuBadges] = useState({
    appointments: 0,
    acceptances: 0,
    quotes: 0,
    partsRequests: 0,
    orders: 0,
    workOrders: 0,
    invoices: 0,
    lowStock: 0,
    richieste: 0,
    ordini: 0
  });

  // Determine nav items based on user type
  const userType = (user?.user_type || "").toUpperCase();
  const userRole = (user?.role || "").toUpperCase();
  const isRicambista = userType === "RICAMBISTA";
  const isAdmin = userRole === "ADMIN";
  const baseNavItems = isRicambista ? ricambistaNavItems : officinaNavItems;
  // Filter RENTRI nav item: admins always see it, others need rentri_enabled
  const navItems = baseNavItems.filter(item => {
    if (item.path === "/rentri" && !isAdmin && !user?.rentri_enabled) return false;
    return true;
  });

  useEffect(() => {
    // Check if PWA install prompt is available
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallPrompt(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Menu badges represent items NEW since the last time the user visited that
  // section. `menuBadges` holds the raw live counts; `badgeSeen` stores the
  // count acknowledged at last visit (persisted per user in localStorage).
  const badgeStorageKey = `psvc_badge_seen_${user?.user_id || "anon"}`;
  const [badgeSeen, setBadgeSeen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(badgeStorageKey) || "{}"); }
    catch { return {}; }
  });

  const persistBadgeSeen = (next) => {
    setBadgeSeen(next);
    try { localStorage.setItem(badgeStorageKey, JSON.stringify(next)); } catch (e) {}
  };

  // Reload the acknowledged baseline once the user is available (fixes badges
  // reappearing after a reload: on first render user_id may be undefined so the
  // initializer read the wrong 'anon' storage key).
  useEffect(() => {
    try {
      setBadgeSeen(JSON.parse(localStorage.getItem(badgeStorageKey) || "{}"));
    } catch { setBadgeSeen({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const getDisplayBadge = (badgeKey) => {
    if (!badgeKey) return 0;
    const raw = menuBadges[badgeKey] || 0;
    const seen = badgeSeen[badgeKey] || 0;
    return Math.max(0, raw - seen);
  };

  // Entering a section (via click or direct URL) clears its badge by
  // acknowledging the current count.
  useEffect(() => {
    const item = navItems.find((i) => i.path === location.pathname && i.badgeKey);
    if (item) {
      const raw = menuBadges[item.badgeKey] || 0;
      if ((badgeSeen[item.badgeKey] || 0) !== raw) {
        persistBadgeSeen({ ...badgeSeen, [item.badgeKey]: raw });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, menuBadges]);

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await authFetch(`${API}/notifications/unread-count`, { });
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.count || 0);
        }
      } catch (e) {
        // Ignore
      }
    };
    fetchUnread();
    fetchMenuBadges();
    // Poll every 30 seconds for real-time effect
    const interval = setInterval(() => {
      fetchUnread();
      fetchMenuBadges();
    }, 30000);
    return () => clearInterval(interval);
  }, [isRicambista]);

  // Fetch menu badges based on user type
  const fetchMenuBadges = async () => {
    try {
      if (isRicambista) {
        // Fetch ricambista stats
        const statsRes = await authFetch(`${API}/ricambista/stats`, { });
        if (statsRes.ok) {
          const stats = await statsRes.json();
          setMenuBadges(prev => ({
            ...prev,
            richieste: stats.pending_requests || 0,
            ordini: stats.orders_today || 0
          }));
        }
      } else {
        // Fetch officina stats
        const statsRes = await authFetch(`${API}/stats/dashboard`, { });
        if (statsRes.ok) {
          const stats = await statsRes.json();
          setMenuBadges(prev => ({
            ...prev,
            appointments: stats.appointments_today || 0,
            acceptances: stats.active_jobs || 0,
            quotes: stats.pending_quotes || 0,
            workOrders: stats.active_jobs || 0,
            invoices: stats.unpaid_invoices || 0
          }));
        }
        
        // Fetch low stock items count
        try {
          const lowStockRes = await authFetch(`${API}/modules/articoli?sotto_scorta=true`, { });
          if (lowStockRes.ok) {
            const items = await lowStockRes.json();
            setMenuBadges(prev => ({
              ...prev,
              lowStock: Array.isArray(items) ? items.length : 0
            }));
          }
        } catch (e) {
          // Ignore low stock fetch errors
        }
        
        // Fetch pending parts requests
        try {
          const partsRes = await authFetch(`${API}/parts-requests?status=pending`, { });
          if (partsRes.ok) {
            const parts = await partsRes.json();
            setMenuBadges(prev => ({
              ...prev,
              partsRequests: Array.isArray(parts) ? parts.length : 0
            }));
          }
        } catch (e) {
          // Ignore parts requests fetch errors
        }
      }
    } catch (error) {
      console.error("Error fetching menu badges:", error);
    }
  };

  // Fetch notifications list (bell shows only UNREAD; reading removes them)
  const fetchNotifications = async () => {
    try {
      const res = await authFetch(`${API}/notifications`, { });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.filter(n => !n.read).slice(0, 15));
      }
    } catch (e) {
      console.error("Error fetching notifications:", e);
    }
  };

  // Mark single notification as read -> remove it from the bell list
  const markAsRead = async (notificationId) => {
    try {
      await authFetch(`${API}/notifications/${notificationId}/read`, {
        method: "PATCH"
      });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error("Error marking notification as read:", e);
    }
  };

  // Delete a single notification
  const deleteNotification = async (notificationId) => {
    try {
      const wasUnread = notifications.find(n => n.notification_id === notificationId && !n.read);
      await authFetch(`${API}/notifications/${notificationId}`, { method: "DELETE" });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error("Error deleting notification:", e);
    }
  };

  // Mark all notifications as read (clears the bell list)
  const markAllAsRead = async () => {
    try {
      await authFetch(`${API}/notifications/read-all`, {
        method: "PATCH"
      });
      setNotifications([]);
      setUnreadCount(0);
      toast.success("Tutte le notifiche sono state lette");
    } catch (e) {
      toast.error("Errore nel contrassegnare le notifiche");
    }
  };

  // Map notification type -> destination route (officina side)
  const getNotificationLink = (notif) => {
    const map = {
      acceptance_created: "/acceptances",
      acceptance_status: "/acceptances",
      parts_response: "/parts-requests",
      request_status_update: "/parts-requests",
      quote_received: "/parts-requests",
      quote_response: "/parts-requests",
      quote_viewed: "/quotes",
      work_order_created: "/work-orders",
      work_order_status: "/work-orders",
      order_received: "/supplier-orders",
      new_order: "/supplier-orders",
      earned: "/points",
      spent: "/points",
      rentri_fir: "/rentri",
      rentri: "/rentri",
    };
    if (notif.type === "user_registered" && notif.data?.registered_user_id) {
      return `/admin/users/${notif.data.registered_user_id}`;
    }
    if (notif.type === "gdpr_delete_request") {
      return "/admin/backups";
    }
    return map[notif.type] || null;
  };

  // Click a notification: mark read, navigate to related page, close dropdown
  const handleNotificationClick = (notif) => {
    if (!notif.read) markAsRead(notif.notification_id);
    const link = getNotificationLink(notif);
    setShowNotifications(false);
    if (link) navigate(link);
  };

  // Delete ALL notifications for the user
  const deleteAllNotifications = async () => {
    try {
      await authFetch(`${API}/notifications`, { method: "DELETE" });
      setNotifications([]);
      setUnreadCount(0);
      toast.success("Notifiche eliminate");
    } catch (e) {
      toast.error("Errore nell'eliminazione delle notifiche");
    }
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('App installata con successo!');
        setShowInstallPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, {
        method: "POST"
      });
      toast.success("Logout effettuato");
      navigate("/");
    } catch (error) {
      toast.error("Errore durante il logout");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="sidebar">
        {/* Logo */}
        <div className="p-4 border-b border-zinc-800">
          <NavLink to={isRicambista ? "/ricambista" : "/dashboard"} className="flex items-center gap-3">
            <img 
              src="/logo-icon.svg" 
              alt="PServicePro" 
              className="w-12 h-12"
            />
            <span className="text-xl font-bold text-white tracking-tight font-['Barlow_Condensed'] hidden md:block">
              PServicePro
            </span>
          </NavLink>
        </div>

        {/* Role Badge */}
        {isRicambista && (
          <div className="px-4 py-2">
            <div className="px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 rounded-sm text-center">
              <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">Ricambista</span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map((item) => {
            const badgeCount = getDisplayBadge(item.badgeKey);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""}`
                }
                data-testid={`nav-${item.path.replace("/", "")}`}
              >
                <item.icon className="w-6 h-6" />
                <span className="hidden md:block text-base flex-1">{item.label}</span>
                {badgeCount > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-medium">
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
          
          {/* Admin Link - visible only to admin/staff */}
          {(user?.role === "admin" || user?.role === "staff" || user?.role?.toLowerCase() === "admin") && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `sidebar-link mt-4 border-t border-zinc-800 pt-4 ${isActive ? "active" : ""}`
              }
              data-testid="nav-admin"
            >
              <Shield className="w-6 h-6 text-purple-400" />
              <span className="hidden md:block text-base text-purple-400">Pannello Admin</span>
            </NavLink>
          )}
        </nav>

        {/* Install PWA Button */}
        {showInstallPrompt && (
          <div className="px-4 py-2">
            <button
              onClick={handleInstallClick}
              className="w-full flex items-center justify-center gap-2 p-3 bg-primary/10 border border-primary/30 rounded-sm text-primary hover:bg-primary/20 transition-colors"
              data-testid="install-pwa-btn"
            >
              <Download className="w-5 h-5" />
              <span className="hidden md:block text-sm font-medium">Installa App</span>
            </button>
          </div>
        )}

        {/* User Section */}
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-2 mb-3">
            {/* Theme Toggle */}
            <button 
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="p-2 rounded-sm hover:bg-zinc-900/50 transition-colors"
              title={theme === "dark" ? "Passa a Light Mode" : "Passa a Dark Mode"}
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5 text-zinc-400 hover:text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-zinc-600 hover:text-zinc-900" />
              )}
            </button>

            {/* Support Request Button */}
            <SupportRequestDialog user={user} triggerClassName="p-2 border-0" />

            {/* Download Manual Button */}
            <button
              data-testid="download-manual-btn"
              onClick={async () => {
                try {
                  const response = await authFetch(`${API}/manual/download`);
                  if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'PService_Pro_Manuale_Utente.pdf';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  }
                } catch (err) {
                  console.error("Download manual error:", err);
                }
              }}
              className="p-2 rounded-sm hover:bg-zinc-900/50 transition-colors"
              title="Scarica Manuale Utente"
            >
              <BookOpen className="w-5 h-5 text-zinc-400 hover:text-primary" />
            </button>

            {/* Notifications Popover */}
            <Popover open={showNotifications} onOpenChange={(open) => {
              setShowNotifications(open);
              if (open) fetchNotifications();
            }}>
              <PopoverTrigger asChild>
                <button 
                  data-testid="notifications-btn"
                  className="relative p-2 rounded-sm hover:bg-zinc-900/50 transition-colors"
                >
                  <Bell className="w-5 h-5 text-zinc-400" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 bg-zinc-900 border-zinc-800 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
                  <h3 className="font-semibold text-white text-sm">Notifiche</h3>
                  <div className="flex items-center gap-1">
                    {notifications.length > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={markAllAsRead}
                        data-testid="mark-all-read-btn"
                        className="text-xs text-primary hover:text-primary/80"
                      >
                        Segna tutte lette
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={deleteAllNotifications}
                      data-testid="delete-all-notifications-btn"
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Cancella tutte
                    </Button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-zinc-500 text-sm">
                      Nessuna notifica
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div 
                        key={notif.notification_id}
                        data-testid={`notification-${notif.notification_id}`}
                        className={`px-4 py-3 border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors ${!notif.read ? 'bg-primary/5' : ''}`}
                      >
                        <div className="flex items-start gap-2">
                          {!notif.read && (
                            <span className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                          )}
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => handleNotificationClick(notif)}
                          >
                            <p className="text-sm text-white font-medium">{notif.title}</p>
                            <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{notif.message}</p>
                            <p className="text-xs text-zinc-600 mt-1">
                              {new Date(notif.created_at).toLocaleDateString('it-IT', { 
                                day: '2-digit', 
                                month: 'short', 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteNotification(notif.notification_id); }}
                            data-testid={`delete-notification-${notif.notification_id}`}
                            className="p-1 rounded hover:bg-zinc-700 text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0"
                            title="Elimina notifica"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                data-testid="user-menu-btn"
                className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-zinc-900/50 transition-colors"
              >
                <Avatar className="w-12 h-12 border border-zinc-700">
                  <AvatarFallback className="bg-primary/10 text-primary font-bold text-lg">
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left hidden md:block">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-zinc-500">@{user?.username}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
              <DropdownMenuItem 
                data-testid="menu-profile"
                onClick={() => navigate("/profile")}
                className="cursor-pointer"
              >
                <User className="w-5 h-5 mr-2" />
                Profilo
              </DropdownMenuItem>
              {user?.role === "admin" && (
                <DropdownMenuItem 
                  data-testid="menu-admin"
                  onClick={() => navigate("/admin")}
                  className="cursor-pointer"
                >
                  <Settings className="w-5 h-5 mr-2" />
                  Admin Panel
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                data-testid="menu-logout"
                onClick={handleLogout}
                className="cursor-pointer text-red-400"
              >
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
